import React from 'react';
import { CATEGORIES } from '../api';

const fmtR = (n) => {
  if (n == null) return '—';
  if (Math.abs(n) >= 1e6) return `R ${(n / 1e6).toFixed(2)}m`;
  if (Math.abs(n) >= 1e3) return `R ${Math.round(n / 1e3)}k`;
  return `R ${Math.round(n)}`;
};

const chipClass = (color) =>
  color === 'green' ? 'green' : color === 'amber' ? 'amber' : 'red';

const catLabel = (key) =>
  CATEGORIES.find(c => c.key === key)?.label || key || '';

function whySentence(dimensions) {
  const known = (dimensions || []).filter(d => d.score != null);
  if (known.length < 2) return null;
  const sorted = [...known].sort((a, b) => b.score - a.score);
  const strong = sorted[0];
  const weak = sorted[sorted.length - 1];
  return (
    <>
      <b>{strong.name}</b> ({strong.score}) carries this site; the binding
      constraint is <b>{weak.name.toLowerCase()}</b> at {weak.score}.
    </>
  );
}

function DimBars({ dimensions }) {
  const known = dimensions.filter(d => d.score != null);
  const max = Math.max(...known.map(d => d.score), 0);
  const min = Math.min(...known.map(d => d.score), 100);
  return dimensions.map(d => {
    const cls = d.score == null ? '' : d.score === min && known.length > 1 ? 'weak'
      : d.score === max ? 'strong' : '';
    return (
      <div key={d.name} className={`sq-dim ${cls}`}>
        <span className="lb">{d.name}</span>
        <span className="tr"><i style={{ width: `${d.score || 0}%` }} /></span>
        <span className="vl">{d.score != null ? d.score : '—'}</span>
      </div>
    );
  });
}

export default function InsightPanel({
  result, loading, error,
  hasPin, category, radiusKm, capture,
  credits, isAdmin,
  onUnlock, unlocking, onBuyCredits,
  onDemo, onDownloadPdf, pdfBusy,
}) {
  // ---------- empty / prompt states ----------
  if (!loading && !result && !error) {
    return (
      <div style={styles.center}>
        <div style={styles.emptyKicker}>SITE INTELLIGENCE</div>
        <div style={styles.emptyTitle}>
          {!category ? 'Choose a category below' : !hasPin ? 'Drop a pin on the map' : 'Scoring…'}
        </div>
        <p style={styles.emptyText}>
          Click anywhere on the map to score a site instantly — demand,
          visibility, competition and cannibalisation, re-scored live as you
          move the pin or change assumptions.
        </p>
        {onDemo && (
          <button className="sq-btn" onClick={onDemo} style={{ marginTop: 16 }}>
            Run demo report
          </button>
        )}
      </div>
    );
  }

  if (error && !loading && !result) {
    return (
      <div style={styles.center}>
        <div style={{ ...styles.emptyTitle, color: 'var(--s-red)' }}>No score for this pin</div>
        <p style={styles.emptyText}>{error}</p>
      </div>
    );
  }

  if (loading && !result) {
    return (
      <div style={{ padding: 22 }}>
        <div className="sq-skel" style={{ width: 110, height: 52, marginBottom: 14 }} />
        <div className="sq-skel" style={{ width: '60%', height: 16, marginBottom: 26 }} />
        {[...Array(7)].map((_, i) => (
          <div key={i} className="sq-skel" style={{ height: 12, marginBottom: 12, width: `${92 - i * 6}%` }} />
        ))}
      </div>
    );
  }

  const r = result;
  const locked = !!r.locked;

  return (
    <div style={{ ...styles.scroll, opacity: loading ? 0.55 : 1 }}>

      {/* ---------- verdict hero ---------- */}
      <div style={styles.hero}>
        <div>
          <div style={styles.bigScore}>
            {locked ? `${r.score_band.low}–${r.score_band.high}` : r.total_score}
          </div>
          <div style={styles.scoreSub}>SITE SCORE / 100</div>
        </div>
        <div style={{ paddingTop: 4 }}>
          <span className={`sq-chip ${chipClass(r.verdict_color)}`}><i />{r.verdict}</span>
          <div style={styles.conf}>
            Confidence <b style={{ color: 'var(--s-text)' }}>{Math.round((r.confidence || 0) * 100)}%</b>
            <br />{catLabel(r.category)} · {radiusKm?.toFixed ? radiusKm.toFixed(1) : radiusKm} km catchment
          </div>
        </div>
      </div>

      {/* ---------- why ---------- */}
      <div style={styles.why}>
        {locked ? (
          <p style={styles.whyText}>
            Strongest dimension: <b>{r.drivers?.strongest || '—'}</b>. Binding
            constraint: <b>{(r.drivers?.weakest || '—').toLowerCase()}</b>.
            {r.competitor_count != null && (
              <> {r.competitor_count} same-category competitors trade in this catchment.</>
            )}
          </p>
        ) : (
          <p style={styles.whyText}>{whySentence(r.dimensions)}</p>
        )}
      </div>

      {/* ---------- score anatomy ---------- */}
      <h3 className="sq-sec">Score anatomy</h3>
      {locked ? (
        <>
          {(r.dimension_names || []).map(name => (
            <div key={name} className="sq-dim locked">
              <span className="lb">{name}</span>
              <span className="tr"><i /></span>
              <span className="vl" style={{ color: 'var(--s-faint)' }}>•</span>
            </div>
          ))}
        </>
      ) : (
        <DimBars dimensions={r.dimensions || []} />
      )}

      {/* ---------- economics ---------- */}
      <h3 className="sq-sec">Economics</h3>
      {locked ? (
        <div style={styles.lockCard}>
          <div style={styles.lockBlur}>
            <div style={styles.statK}>Predicted revenue @ {capture?.toFixed?.(1) || capture}%</div>
            <div style={styles.statV}>R •.••m – •.••m <small style={styles.statSm}>/mo</small></div>
            <div style={{ ...styles.statK, marginTop: 12 }}>Cannibalisation vs your stores</div>
            <div style={styles.statV}>−••%</div>
          </div>
          <div style={styles.lockInner}>
            <div style={styles.lockTitle}>Unlock the full report</div>
            <div style={styles.lockText}>
              Exact score, revenue prediction, cannibalisation against your own
              stores, and network ranking — unlocked for this site for 30 days,
              with unlimited assumption changes.
            </div>
            {credits > 0 || isAdmin ? (
              <button className="sq-btn primary" onClick={onUnlock} disabled={unlocking} style={{ width: '100%' }}>
                {unlocking ? 'Unlocking…' : isAdmin ? 'Unlock full report (admin)' : `Unlock — 1 credit (${credits} left)`}
              </button>
            ) : (
              <button className="sq-btn primary" onClick={onBuyCredits} style={{ width: '100%' }}>
                Buy report credits
              </button>
            )}
          </div>
        </div>
      ) : (
        <>
          <div style={styles.statRow}>
            <div style={styles.stat}>
              <div style={styles.statK}>Catchment spend</div>
              <div style={styles.statV}>{fmtR(r.rep?.total)}<small style={styles.statSm}> /mo</small></div>
            </div>
            <div style={{ ...styles.stat, background: 'rgba(255,106,61,0.08)', borderColor: 'rgba(255,106,61,0.25)' }}>
              <div style={styles.statK}>Revenue @ {capture?.toFixed?.(1) || capture}%</div>
              <div style={styles.statV}>
                {fmtR((r.rep?.total || 0) * capture / 100 * 0.82)}–{fmtR((r.rep?.total || 0) * capture / 100 * 1.18)}
                <small style={styles.statSm}> /mo</small>
              </div>
            </div>
          </div>

          {(r.rep?.own_brand_count || 0) > 0 && (
            <div style={styles.cannRow}>
              <span style={{ color: 'var(--s-muted)', fontSize: 12.5 }}>
                Cannibalisation — {r.rep.own_brand_count} own store{r.rep.own_brand_count > 1 ? 's' : ''} in catchment
              </span>
              <span style={{ font: '600 13px var(--s-mono)', color: 'var(--s-red)' }}>
                −{(r.rep.cannibalisation_pct || 0).toFixed(0)}%
              </span>
            </div>
          )}
          {r.saturation && (
            <div style={styles.satNote}>
              {r.saturation.competitor_count} competitors · {r.saturation.hh_per_outlet?.toLocaleString?.() || '—'} households
              per outlet · market {String(r.saturation.verdict || '').toLowerCase()}
            </div>
          )}
        </>
      )}

      {/* ---------- network ranking ---------- */}
      {!locked && r.network_ranking && r.network_ranking.rank != null && (
        <>
          <h3 className="sq-sec">Network ranking</h3>
          <div style={{ fontSize: 13, color: 'var(--s-muted)' }}>
            This site would rank{' '}
            <b style={{ color: 'var(--s-text)', fontFamily: 'var(--s-mono)' }}>
              #{r.network_ranking.rank}
            </b>{' '}
            of {r.network_ranking.total_stores ?? r.network_ranking.total ?? '—'} stores in your network.
          </div>
        </>
      )}

      {/* ---------- benchmark ---------- */}
      {!locked && r.benchmark && r.benchmark.benchmark_count > 0 && (
        <>
          <h3 className="sq-sec">Nearby benchmark · ranks #{r.benchmark.score_rank} of {r.benchmark.benchmark_count + 1}</h3>
          {(r.benchmark.sites || []).slice(0, 4).map(s => (
            <div key={s.name} style={styles.rowItem}>
              <span style={styles.rowName}>
                {s.name}
                <small style={styles.rowSub}>{s.dist_km} km away</small>
              </span>
              <span style={styles.rowVal}>{s.score}</span>
            </div>
          ))}
        </>
      )}

      {/* ---------- competitors ---------- */}
      {(r.competitors || []).length > 0 && (
        <>
          <h3 className="sq-sec">
            Competitors · {locked ? r.competitor_count : r.competitors.length} in catchment
          </h3>
          {r.competitors.slice(0, 6).map((c, i) => (
            <div key={`${c.name}-${i}`} style={styles.rowItem}>
              <span style={{
                ...styles.threatDot,
                background: c.threat === 'High' ? 'var(--s-red)'
                  : c.threat === 'Medium' ? 'var(--s-amber)'
                  : c.threat === 'Own Brand' ? 'var(--s-blue)' : 'var(--s-faint)',
              }} />
              <span style={styles.rowName}>
                {c.brand}
                <small style={styles.rowSub}>{c.name}</small>
              </span>
              <span style={styles.rowVal}>{c.dist_km} km</span>
            </div>
          ))}
        </>
      )}

      {/* ---------- provenance + actions ---------- */}
      <div style={styles.prov}>
        Census subplace demographics · SACSC trading densities · verified POIs
        {r.dominant_lsm ? ` · dominant ${r.dominant_lsm}` : ''}
        {locked ? ' · free preview' : ''}
      </div>

      {!locked && r.report_id && (
        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
          <button className="sq-btn" style={{ flex: 1 }} onClick={onDownloadPdf} disabled={pdfBusy}>
            {pdfBusy ? 'Rendering…' : 'Download PDF'}
          </button>
        </div>
      )}
    </div>
  );
}

const styles = {
  scroll: { padding: '22px 22px 18px', transition: 'opacity 0.2s' },
  center: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center',
    padding: '0 26px',
  },
  emptyKicker: {
    font: '600 10px var(--s-sans)',
    letterSpacing: '0.2em',
    color: 'var(--s-accent)',
    marginBottom: 10,
  },
  emptyTitle: {
    fontFamily: 'var(--s-serif)',
    fontSize: 24,
    fontWeight: 700,
    color: 'var(--s-text)',
    lineHeight: 1.2,
    marginBottom: 10,
  },
  emptyText: { fontSize: 13, color: 'var(--s-muted)', lineHeight: 1.7 },

  hero: { display: 'flex', gap: 18, alignItems: 'flex-start' },
  bigScore: {
    font: '700 52px/1 var(--s-mono)',
    letterSpacing: '-0.04em',
    color: 'var(--s-text)',
    whiteSpace: 'nowrap',
  },
  scoreSub: { font: '500 10px var(--s-mono)', color: 'var(--s-faint)', marginTop: 6, letterSpacing: '0.1em' },
  conf: { fontSize: 11.5, color: 'var(--s-muted)', marginTop: 8, lineHeight: 1.6 },

  why: { margin: '16px 0 2px', borderTop: '1px solid var(--s-line2)', paddingTop: 14 },
  whyText: {
    fontFamily: 'var(--s-serif)',
    fontSize: 15,
    lineHeight: 1.55,
    color: '#d8d4ca',
  },

  statRow: { display: 'flex', gap: 10 },
  stat: {
    flex: 1,
    border: '1px solid var(--s-line2)',
    borderRadius: 11,
    padding: '11px 13px',
    background: 'var(--s-w025)',
  },
  statK: {
    fontSize: 10.5,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: 'var(--s-faint)',
    marginBottom: 5,
  },
  statV: { font: '600 16px var(--s-mono)', letterSpacing: '-0.02em', color: 'var(--s-text)' },
  statSm: { fontSize: 11, color: 'var(--s-muted)', fontWeight: 500 },

  cannRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 10,
    padding: '9px 13px',
    border: '1px solid rgba(255,93,93,0.25)',
    borderRadius: 11,
    background: 'rgba(255,93,93,0.06)',
  },
  satNote: { fontSize: 11.5, color: 'var(--s-faint)', marginTop: 10, lineHeight: 1.6 },

  lockCard: {
    position: 'relative',
    border: '1px solid var(--s-line)',
    borderRadius: 12,
    overflow: 'hidden',
  },
  lockBlur: {
    padding: '14px 16px',
    filter: 'blur(7px)',
    userSelect: 'none',
    pointerEvents: 'none',
    opacity: 0.8,
  },
  lockInner: {
    borderTop: '1px solid var(--s-line)',
    padding: '14px 16px 16px',
    background: 'rgba(255,106,61,0.05)',
  },
  lockTitle: { fontWeight: 700, fontSize: 14, color: 'var(--s-text)', marginBottom: 6 },
  lockText: { fontSize: 12.5, color: 'var(--s-muted)', lineHeight: 1.6, marginBottom: 12 },

  rowItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 11,
    padding: '8px 0',
    borderBottom: '1px solid var(--s-line2)',
  },
  rowName: { flex: 1, fontSize: 13, color: 'var(--s-text)', minWidth: 0 },
  rowSub: { display: 'block', color: 'var(--s-faint)', fontSize: 11, marginTop: 1 },
  rowVal: { font: '600 12.5px var(--s-mono)', color: 'var(--s-muted)', whiteSpace: 'nowrap' },
  threatDot: { width: 8, height: 8, borderRadius: '50%', flexShrink: 0 },

  prov: {
    marginTop: 22,
    paddingTop: 14,
    borderTop: '1px solid var(--s-line2)',
    font: '400 10.5px var(--s-mono)',
    color: 'var(--s-faint)',
    lineHeight: 1.7,
  },
};
